/**
 * Главный компонент приложения
 * Автор: Cursor Generator
 * Описание: Роутинг и основная структура приложения
 */

import { useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import axios from 'axios';
import Layout from './components/Layout';
import AuthGuard from './components/AuthGuard';
import HomePage from './pages/HomePage';
import Dashboard from './pages/Dashboard';
import Channels from './pages/Channels';
import Sources from './pages/Sources';
import Export from './pages/Export';
import Settings from './pages/Settings';
import YouTube from './pages/YouTube';
import AuthCallback from './pages/AuthCallback';
import { initNativeApp, isNative } from './utils/native';
import { authHelpers } from './lib/supabase.js';

// Глобальный axios interceptor для автоматической передачи JWT токена
axios.interceptors.request.use(
  async (config) => {
    try {
      // Пытаемся получить JWT токен от Supabase
      const accessToken = await authHelpers.getAccessToken();
      if (accessToken) {
        config.headers['Authorization'] = `Bearer ${accessToken}`;
      } else {
        // Fallback на старую систему session_token
        const sessionToken = localStorage.getItem('session_token');
        if (sessionToken) {
          config.headers['x-session-token'] = sessionToken;
        }
      }
    } catch (error) {
      // Если ошибка — используем старую систему
      const sessionToken = localStorage.getItem('session_token');
      if (sessionToken) {
        config.headers['x-session-token'] = sessionToken;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

function App() {
  // Инициализация нативных функций при запуске
  useEffect(() => {
    const init = async () => {
      try {
        await initNativeApp();
        console.log('[App] Нативные функции инициализированы');
      } catch (error) {
        console.warn('[App] Ошибка инициализации нативных функций:', error);
      }
    };
    
    // Проверяем и очищаем черновики YouTube настроек при загрузке приложения
    // если они принадлежат другому пользователю
    const checkAndCleanYouTubeDrafts = async () => {
      try {
        const saved = sessionStorage.getItem('youtube_settings_draft');
        if (saved) {
          const parsed = JSON.parse(saved);
          
          // Если нет user_id в сохраненных данных - очищаем (старый формат)
          if (!parsed.user_id) {
            console.log('[App] Обнаружены черновики YouTube настроек без user_id (старый формат), очищаем sessionStorage');
            sessionStorage.removeItem('youtube_settings_draft');
            return;
          }
          
          // Пытаемся получить текущего пользователя
          try {
            const userStr = localStorage.getItem('user');
            if (userStr) {
              const user = JSON.parse(userStr);
              if (user.id && parsed.user_id !== user.id) {
                console.log('[App] Обнаружены черновики YouTube настроек другого пользователя, очищаем sessionStorage', {
                  savedUserId: parsed.user_id,
                  currentUserId: user.id
                });
                sessionStorage.removeItem('youtube_settings_draft');
              }
            } else {
              // Пользователь не авторизован - очищаем черновики
              console.log('[App] Пользователь не авторизован, очищаем черновики YouTube настроек');
              sessionStorage.removeItem('youtube_settings_draft');
            }
          } catch (e) {
            console.warn('[App] Ошибка проверки пользователя для черновиков:', e);
            // При ошибке тоже очищаем для безопасности
            sessionStorage.removeItem('youtube_settings_draft');
          }
        }
      } catch (e) {
        console.warn('[App] Ошибка проверки черновиков YouTube настроек:', e);
        // При ошибке парсинга очищаем для безопасности
        try {
          sessionStorage.removeItem('youtube_settings_draft');
        } catch (clearErr) {
          console.warn('[App] Ошибка очистки sessionStorage:', clearErr);
        }
      }
    };
    
    // Отслеживаем изменения localStorage.user для очистки sessionStorage при смене пользователя
    const checkUserChange = () => {
      try {
        const userStr = localStorage.getItem('user');
        const saved = sessionStorage.getItem('youtube_settings_draft');
        
        if (saved) {
          const parsed = JSON.parse(saved);
          
          // Если нет user_id в сохраненных данных - очищаем
          if (!parsed.user_id) {
            sessionStorage.removeItem('youtube_settings_draft');
            return;
          }
          
          if (userStr) {
            const user = JSON.parse(userStr);
            // Если user_id не совпадает - очищаем
            if (user.id && parsed.user_id !== user.id) {
              console.log('[App] Обнаружена смена пользователя, очищаем черновики YouTube настроек', {
                savedUserId: parsed.user_id,
                currentUserId: user.id
              });
              sessionStorage.removeItem('youtube_settings_draft');
            }
          } else {
            // Пользователь не авторизован - очищаем
            sessionStorage.removeItem('youtube_settings_draft');
          }
        }
      } catch (e) {
        console.warn('[App] Ошибка проверки смены пользователя:', e);
      }
    };
    
    // Проверяем при загрузке
    checkUserChange();
    
    // Отслеживаем изменения localStorage через событие storage
    const handleStorageChange = (e) => {
      if (e.key === 'user') {
        console.log('[App] Обнаружено изменение localStorage.user, проверяем черновики YouTube настроек');
        checkUserChange();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Также проверяем периодически (на случай, если событие storage не сработало)
    const userCheckInterval = setInterval(checkUserChange, 5000);
    
    init();
    checkAndCleanYouTubeDrafts();
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(userCheckInterval);
    };
  }, []);

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/home" element={<HomePage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/dashboard"
        element={
          <AuthGuard>
            <Layout>
              <Dashboard />
            </Layout>
          </AuthGuard>
        }
      />
      <Route
        path="/channels"
        element={
          <AuthGuard>
            <Layout>
              <Channels />
            </Layout>
          </AuthGuard>
        }
      />
      <Route
        path="/sources"
        element={
          <AuthGuard>
            <Layout>
              <Sources />
            </Layout>
          </AuthGuard>
        }
      />
      <Route
        path="/youtube"
        element={
          <AuthGuard>
            <Layout>
              <YouTube />
            </Layout>
          </AuthGuard>
        }
      />
      <Route
        path="/export"
        element={
          <AuthGuard>
            <Layout>
              <Export />
            </Layout>
          </AuthGuard>
        }
      />
      <Route
        path="/settings"
        element={
          <AuthGuard>
            <Layout>
              <Settings />
            </Layout>
          </AuthGuard>
        }
      />
    </Routes>
  );
}

export default App;


